import {
  type Maybe,
  type OrganizationMembersTableQuery,
  type User,
  type UserActivityDetails,
} from '../../generated/graphql';
import {type LicenseSeatsResult} from '../../pages/OrgDashboard/UserActivityDashboard/UserSeatsCard/useLicenseSeatsResult';
import {
  getPrimarySub,
  type HasSubsWithPlanType,
} from '../../util/accounts/pricing';
import {type RoleOption} from './TeamOrOrganizationRoleOption';

export enum UserRole {
  Member = 'Member',
  Admin = 'Admin',
  Viewer = 'Viewer',
  Service = 'Service',
}

export enum UserStatus {
  Pending = 'Pending',
  Active = 'Active',
  Deactivated = 'Deactivated',
  Unknown = 'Unknown',
}

export type LastActiveSort = {
  activity?: Pick<User, 'activity'>['activity'];
  createdAt: string;
  status: UserStatus;
};

export type TableUser = Pick<
  User,
  'id' | 'email' | 'photoUrl' | 'username' | 'activity'
> & {
  createdAt: string;
  displayName: string;
  name?: string;
  role: UserRole;
  status: UserStatus;
  teams: Array<{id: string; name: string}>;
  modelsSeat?: ModelsSeatType;
  weaveRole?: WeaveRoleType;
  organization?: {
    id?: string;
    name?: string;
  };
  lastActiveAt: LastActiveSort; // values off of the user object needed for sorting
};

export enum ModelsSeatType {
  FULL = 'Full',
  VIEWER = 'Viewer',
  NOACCESS = 'No access',
}

export enum OrganizationMemberRole {
  MEMBER = 'member',
  ADMIN = 'admin',
  VIEW_ONLY = 'viewer',
  BILLING_ONLY = 'billing-only',
}

export function getModelsSeatTypeColumnName(seat: ModelsSeatType) {
  switch (seat) {
    case ModelsSeatType.VIEWER:
      return 'viewer';
    case ModelsSeatType.NOACCESS:
      return 'none';
    case ModelsSeatType.FULL:
      return 'full';
  }
}

export function getWeaveRoleTypeColumnName(seat: WeaveRoleType) {
  switch (seat) {
    case WeaveRoleType.VIEWER:
      return 'viewer';
    case WeaveRoleType.NOACCESS:
      return 'none';
    case WeaveRoleType.INCLUDED:
      return 'full';
  }
}

export type ModelSeatTypeColumnName = 'viewer' | 'none' | 'full';
export function getModelsSeatTypeDisplayName(seat: ModelSeatTypeColumnName) {
  switch (seat) {
    case 'viewer':
      return ModelsSeatType.VIEWER;
    case 'none':
      return ModelsSeatType.NOACCESS;
    case 'full':
      return ModelsSeatType.FULL;
  }
}

export function getWeaveRoleTypeDisplayName(seat: string) {
  switch (seat) {
    case 'viewer':
      return WeaveRoleType.VIEWER;
    case 'none':
      return WeaveRoleType.NOACCESS;
    case 'full':
      return WeaveRoleType.INCLUDED;
    default:
      return WeaveRoleType.NOACCESS;
  }
}

export enum WeaveRoleType {
  INCLUDED = 'Full',
  VIEWER = 'Viewer',
  NOACCESS = 'No access',
}

export function transformToTableUser(
  member: NonNullable<
    OrganizationMembersTableQuery['organization']
  >['members'][number]
): TableUser {
  const teams =
    member.teams?.edges
      .map(edge => ({
        id: edge.node?.id ?? '',
        name: edge.node?.name ?? '',
      }))
      .filter(team => team.id && team.name) || [];
  const tableUser = {
    id: member.id,
    email: member.email,
    photoUrl: member.photoUrl,
    username: member.username,
    activity: {lastActiveAt: member.lastActiveAt} as UserActivityDetails, // Partially setting just for last active value
    createdAt: new Date().toISOString(),
    displayName: member.name,
    name: member.name,
    role: member.role as UserRole,
    modelsSeat: member.modelsSeat as ModelsSeatType,
    weaveRole: member.weaveRole as WeaveRoleType,
    status: 'Active' as UserStatus,
    teams,
  };

  return {...tableUser, lastActiveAt: {...tableUser}};
}

type HasMembers = {
  members: Array<{}>;
};
type HasOrgRole = {role: string};
type HasUsername = {username: string};
type HasBillingUser = {
  billingUser?: Maybe<{username?: Maybe<string>}>;
};

export function getIsMemberBillingUser<
  T extends HasUsername,
  U extends HasBillingUser,
>(member?: T, organization?: U): boolean {
  if (member == null || organization == null) {
    return false;
  }

  return member.username === organization.billingUser?.username;
}

type HasSubsWithPrivilege = {
  subscriptions?: Array<{privileges?: {num_viewonly_seats?: number}}>;
};

function hasAvailableViewOnlySeats<
  T extends HasSubsWithPlanType & HasSubsWithPrivilege,
>(organization: T) {
  const primarySub = getPrimarySub(organization);
  const numViewOnlySeats = primarySub?.privileges?.num_viewonly_seats;

  if (numViewOnlySeats == null || typeof numViewOnlySeats !== 'number') {
    return false;
  }

  return numViewOnlySeats > 0;
}

export function getOrgRoleDropdownItems<
  T extends HasMembers & HasBillingUser,
  U extends HasUsername & HasOrgRole,
>(organization: T, member: U, roleManagementEnabled: boolean): RoleOption[] {
  // if there is only one member in org, the only option is admin
  const isAdminOnlyOption = organization.members.length === 1;
  const isMemberBillingUser = getIsMemberBillingUser(member, organization);

  const roleOptions: RoleOption[] = [
    {
      text: 'Admin',
      value: OrganizationMemberRole.ADMIN,
      isPremiumOption: false,
    },
    {
      text: isMemberBillingUser ? 'Billing only' : 'Member',
      value: isMemberBillingUser
        ? OrganizationMemberRole.BILLING_ONLY
        : OrganizationMemberRole.MEMBER,
      isPremiumOption: true,
      isPremiumFeatureDisabled: !roleManagementEnabled,
      isDisabled: isAdminOnlyOption || !roleManagementEnabled,
    },
  ];

  return roleOptions;
}

export type UserRoleOption = RoleOption & {value: UserRole};

export function getOrgRoleDropdownItemsServer(): UserRoleOption[] {
  const roleOptions: UserRoleOption[] = [
    {
      text: UserRole.Admin,
      value: UserRole.Admin,
      isPremiumOption: false,
    },
    {
      text: UserRole.Member,
      value: UserRole.Member,
      isPremiumOption: false,
    },
  ];
  return roleOptions;
}

export function getModelSeatTypeDropdownItemsServer(
  seatLicense: LicenseSeatsResult
): RoleOption[] {
  const {licenseSeatsData} = seatLicense;

  const includesViewOnly =
    (licenseSeatsData?.numViewOnlySeatsLicensed &&
      licenseSeatsData?.numViewOnlySeatsLicensed > 0) === true;

  const roleOptions: RoleOption[] = [
    {
      text: 'Full',
      value: ModelsSeatType.FULL,
      isPremiumOption: false,
    },
    {
      text: 'Viewer',
      value: ModelsSeatType.VIEWER,
      isPremiumOption: true,
      isPremiumFeatureDisabled: !includesViewOnly,
      isDisabled: !includesViewOnly,
    },
    {
      text: 'No access',
      value: ModelsSeatType.NOACCESS,
      isPremiumOption: false,
    },
  ];

  return roleOptions;
}

export function getModelSeatTypeDropdownItems<
  T extends HasSubsWithPlanType & HasSubsWithPrivilege,
  U extends HasUsername & HasOrgRole,
>(
  organization: T,
  member: U | null,
  roleManagementEnabled: boolean
): RoleOption[] {
  const includesViewOnly = hasAvailableViewOnlySeats(organization);
  const isMemberBillingOnly =
    member?.role === OrganizationMemberRole.BILLING_ONLY;

  const roleOptions: RoleOption[] = [
    {
      text: 'Full',
      value: ModelsSeatType.FULL,
      isPremiumOption: false,
      isDisabled: isMemberBillingOnly,
      ...(isMemberBillingOnly && {
        disabledToolTipText: 'Billing only users cannot have Models access',
      }),
    },
    {
      text: 'Viewer',
      value: ModelsSeatType.VIEWER,
      isPremiumOption: true,
      isPremiumFeatureDisabled: !roleManagementEnabled || !includesViewOnly,
      isDisabled:
        !roleManagementEnabled || !includesViewOnly || isMemberBillingOnly,
      ...(isMemberBillingOnly && {
        disabledToolTipText: 'Billing only users cannot have Models access',
      }),
    },
    {
      text: 'No access',
      value: ModelsSeatType.NOACCESS,
      isPremiumOption: false,
    },
  ];

  return roleOptions;
}

export const weaveRoleDropdownItems: RoleOption[] = [
  {
    text: 'Full',
    value: WeaveRoleType.INCLUDED,
    isPremiumOption: false,
  },
  {
    text: 'Viewer',
    value: WeaveRoleType.VIEWER,
    isPremiumOption: false,
  },
  {
    text: 'No access',
    value: WeaveRoleType.NOACCESS,
    isPremiumOption: false,
  },
];

export function getWeaveRoleDropdownItems<U extends HasUsername & HasOrgRole>(
  member: U | null,
  isEnterprise: boolean
): RoleOption[] {
  const isMemberBillingOnly =
    member?.role === OrganizationMemberRole.BILLING_ONLY;

  const roleOptions: RoleOption[] = [
    {
      text: 'Full',
      value: WeaveRoleType.INCLUDED,
      isPremiumOption: false,
      isDisabled: isMemberBillingOnly,
      ...(isMemberBillingOnly && {
        disabledToolTipText: 'Billing only users cannot have Weave access',
      }),
    },
    {
      text: 'Viewer',
      value: WeaveRoleType.VIEWER,
      isPremiumOption: true,
      isPremiumFeatureDisabled: !isEnterprise,
      isDisabled: !isEnterprise || isMemberBillingOnly,
      ...(isMemberBillingOnly && {
        disabledToolTipText: 'Billing only users cannot have Weave access',
      }),
    },
    {
      text: 'No access',
      value: WeaveRoleType.NOACCESS,
      isPremiumOption: false,
    },
  ];

  return roleOptions;
}

export function getInviteRoleOptions(
  isViewOnlyAvailable: boolean,
  roleManagementEnabled: boolean
): RoleOption[] {
  const roleOptions: RoleOption[] = [
    {
      text: 'Member',
      value: OrganizationMemberRole.MEMBER,
      isPremiumOption: true,
      isPremiumFeatureDisabled: !roleManagementEnabled,
      isDisabled: !roleManagementEnabled,
    },
    {
      text: 'Admin',
      value: OrganizationMemberRole.ADMIN,
      isPremiumOption: false,
    },
    {
      text: 'Viewer',
      value: OrganizationMemberRole.VIEW_ONLY,
      isPremiumOption: true,
      isPremiumFeatureDisabled: !roleManagementEnabled || !isViewOnlyAvailable,
      isDisabled: !roleManagementEnabled || !isViewOnlyAvailable,
    },
  ];

  return roleOptions;
}

export function getInviteRoleOptionsServer(
  basicUserRole: 'Member' | 'User',
  shouldEnforceSeatLimit: boolean,
  viewerLimitReached: boolean
): RoleOption[] {
  const roleOptions: RoleOption[] = [
    {
      text: basicUserRole,
      value: basicUserRole,
      isPremiumOption: false,
      isDisabled: false,
    },
    {
      text: 'Admin',
      value: 'Admin',
      isPremiumOption: false,
      isDisabled: false,
    },
    {
      text: 'Viewer',
      value: 'Viewer',
      isPremiumOption: false,
      isDisabled: shouldEnforceSeatLimit && viewerLimitReached,
    },
  ];

  return roleOptions;
}
